import { WorkflowNodeType, WorkflowNodeValidationStatus } from '../../models/workflow-builder.model';

/**
 * Modelo de dominio del grafo en el nuevo builder de workflow.
 *
 * Se calca del builder de chatflow (`chatflows/chatflow-builder/models/flow-graph.model.ts`)
 * y se adapta a las particularidades de workflow:
 *
 * Regla de identidad (ver CONTRATO API de PUT /builder/graph y `WorkflowBuilderService.saveGraph`):
 * - Los NODOS se identifican por `nodeKey`. El backend hace match por id o por nodeKey
 *   (`syncNodes`) y preserva el id de BD entre guardados. `id` es null hasta el primer
 *   guardado.
 * - Las ARISTAS (`WorkflowEdge`) NO tienen identidad estable: `saveGraph` hace
 *   `deleteByWorkflowVersion` + recrea, así que sus ids ROTAN en cada guardado. Por eso
 *   aquí se referencian por `nodeKey` + `branchKey` y NUNCA se persiste el id de arista
 *   en el frontend (idéntico al criterio de chatflow con sus transiciones).
 *
 * PUERTO = `branchKey` (gotcha §6 del análisis): cada arista nace del puerto de salida
 * del nodo origen, y ese puerto ES el `branchKey` (DEFAULT tras ACTION; YES/NO tras
 * CONDITION; success/timeout tras WAIT UNTIL_CONDITION; ramas dinámicas en MULTI_BRANCH).
 */
export interface WorkflowGraphNode {
  /** id de BD; null mientras el nodo no se haya guardado nunca. */
  id: number | null;
  nodeKey: string;
  nodeType: WorkflowNodeType;
  /** Subtipo (~25 valores en ACTION); null para END/ANNOTATION sin subtipo. */
  nodeSubtype: string | null;
  title: string;
  description: string | null;
  /** configJson ya parseado. Se serializa solo al enviar al backend. */
  config: Record<string, unknown>;
  positionX: number;
  positionY: number;
  validationStatus: WorkflowNodeValidationStatus;
  isActive: boolean;
}

export interface WorkflowGraphEdge {
  sourceNodeKey: string;
  targetNodeKey: string;
  /**
   * PUERTO de salida del nodo origen (`branchKey` del WorkflowEdge). Es la rama que el
   * runtime resuelve en `selectEdgeForBranch`: si nace con el branchKey equivocado, el
   * motor no encuentra el camino (gotcha §6). Se normaliza a 'DEFAULT' cuando el backend
   * devuelve null.
   */
  branchKey: string;
  label: string;
  sortOrder: number;
  /** configJson de la arista ya parseado (raro, pero el contrato lo admite). */
  config: Record<string, unknown>;
}

export type WorkflowGraphSelection =
  | { kind: 'node'; nodeKey: string }
  | { kind: 'edge'; edgeId: string }
  | null;

/** Estado del indicador de persistencia de la barra superior. */
export type WorkflowSaveState = 'idle' | 'unsaved' | 'saving' | 'saved' | 'error';

export interface WorkflowGraphSnapshot {
  nodes: WorkflowGraphNode[];
  edges: WorkflowGraphEdge[];
}

/**
 * Id local y estable de una arista. Se deriva de origen + puerto + destino, que es
 * justamente lo que no cambia entre guardados (a diferencia del id de BD, que rota).
 */
export function buildEdgeId(edge: Pick<WorkflowGraphEdge, 'sourceNodeKey' | 'branchKey' | 'targetNodeKey'>): string {
  return `${edge.sourceNodeKey}|${edge.branchKey}|${edge.targetNodeKey}`;
}
